import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'

declare global {
    interface Window {
        dataLayer: Record<string, any>[]
    }
}

@Injectable({
    providedIn: 'root',
})
export class GtmService {
    private loaded = false
    private eventQueue: Record<string, any>[] = []

    constructor(@Inject(DOCUMENT) private document: Document) {}

    /**
     * Loads the Google Tag Manager container.
     * Safe to call multiple times.
     */
    load(gtmId: string): void {
        if (this.loaded) {
            return
        }

        if (!this.isValidGtmId(gtmId)) {
            console.warn(`[GTM] Invalid GTM ID: ${gtmId}`)
            return
        }

        window.dataLayer = window.dataLayer || []

        window.dataLayer.push({
            'gtm.start': Date.now(),
            event: 'gtm.js',
        })

        const script = this.document.createElement('script')

        script.async = true
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`

        this.document.head.appendChild(script)

        //commented out as the app won't run without javascript anyway so this isn't necessary
        //this.injectNoScript(gtmId)

        this.loaded = true

        // Flush any queued events
        while (this.eventQueue.length > 0) {
            window.dataLayer.push(this.eventQueue.shift()!)
        }
    }

    /**
     * Push a custom event to the dataLayer.
     */
    push(event: Record<string, any>): void {
        if (!event) {
            return
        }

        if (!window.dataLayer) {
            window.dataLayer = []
        }

        if (!this.loaded) {
            this.eventQueue.push(event)
            return
        }

        window.dataLayer.push(event)
    }

    /**
     * Helper for page views.
     */
    trackPageView(path: string): void {
        this.push({
            event: 'page_view',
            page_path: path,
        })
    }

    /**
     * Generic helper.
     */
    trackEvent(eventName: string, data?: Record<string, any>): void {
        this.push({
            event: eventName,
            ...(data ?? {}),
        })
    }

    /**
     * Whether GTM has been loaded.
     */
    get isLoaded(): boolean {
        return this.loaded
    }

    /**
     * Inject the noscript iframe.
     */
    private injectNoScript(gtmId: string): void {
        const noscript = this.document.createElement('noscript')

        noscript.innerHTML = `
            <iframe
                src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
                height="0"
                width="0"
                style="display:none;visibility:hidden">
            </iframe>
        `

        this.document.body.prepend(noscript)
    }

    /**
     * Validate GTM container IDs.
     */
    private isValidGtmId(gtmId: string): boolean {
        return /^GTM-[A-Z0-9]+$/i.test(gtmId)
    }
}
